import '../spoolmark-landing.css';

import {
  Apple,
  ArrowRight,
  ArrowUpRight,
  Camera,
  CircleCheck,
  DatabaseBackup,
  Mail,
  Moon,
  Play,
  Printer,
  QrCode,
  ScanLine,
  ShieldCheck,
} from 'lucide-react';

const googlePlayUrl =
  'https://play.google.com/store/apps/details?id=io.alxnsv.bambu_spool_scanner';

const productSteps = [
  {
    number: '01',
    label: 'Inventory',
    title: 'Find the spool you need.',
    text: 'See your filament grouped by material. Search by material, color, or location to find a spool, see where it is stored, and check its remaining weight.',
    image: '/images/spoolmark/inventory-light.webp',
    alt: 'Spoolmark inventory showing usable spools grouped by material, with colors, storage locations, and remaining weights',
    caption: 'Your inventory · Light theme',
  },
  {
    number: '02',
    label: 'Track',
    title: 'Check before you print.',
    text: 'Weigh a spool or log the grams used by a print to keep its remaining amount up to date. Check the history of prints and weight changes when planning the next job.',
    image: '/images/spoolmark/print-history-light.webp',
    alt: 'Spoolmark print history showing a cable organizer print using 42 grams and 678 grams remaining',
    caption: 'Print history · Light theme',
  },
  {
    number: '03',
    label: 'Compare',
    title: 'Choose your next filament.',
    text: 'Compare materials, diameters, and printing and drying temperatures side by side. See what changes before trying a different filament.',
    image: '/images/spoolmark/comparison-dark.webp',
    alt: 'Spoolmark filament comparison showing two catalog filaments side by side in dark theme',
    caption: 'Filament comparison · Dark theme',
  },
];

function StoreLinks({ label }: { label: string }) {
  return (
    <nav className="sp-store-links" aria-label={label}>
      <a
        className="sp-store-link sp-store-link-live"
        href={googlePlayUrl}
        target="_blank"
        rel="noreferrer"
        aria-label="Get Spoolmark on Google Play (opens in a new tab)"
      >
        <Play size={19} fill="currentColor" aria-hidden="true" />
        <span>
          <small>Available now</small>
          <strong>Google Play</strong>
        </span>
        <ArrowUpRight size={17} aria-hidden="true" />
      </a>
      <a
        className="sp-store-link sp-store-link-soon"
        href="#iphone-availability"
        aria-label="App Store listing coming soon; see iPhone availability"
      >
        <Apple size={20} aria-hidden="true" />
        <span>
          <small>Coming soon</small>
          <strong>App Store</strong>
        </span>
        <ArrowRight size={17} aria-hidden="true" />
      </a>
    </nav>
  );
}

export function SpoolmarkPage() {
  return (
    <main id="content" className="spoolmark-main sp-page" tabIndex={-1}>
      <section className="sp-hero" id="availability">
        <div className="shell sp-hero-grid">
          <div className="sp-hero-copy">
            <p className="sp-kicker">
              <span className="sp-status-dot" aria-hidden="true" />
              Android available now · iPhone coming soon
            </p>
            <h1>Know every spool.</h1>
            <p className="sp-hero-lead">
              Spoolmark is a local-first filament inventory for mixed-brand
              shelves. Add spools in batches, print QR labels that lead back to
              the right spool, and keep track of what is left for your next
              print.
            </p>
            <StoreLinks label="Get Spoolmark" />
            <p className="sp-listing-note">
              On Google Play, the current listing appears as{' '}
              <strong>Spool Scanner (Spoolmark)</strong> while the product name
              changes. The screens and feature tour below show the upcoming
              iPhone version; features vary by platform and release.
            </p>
          </div>

          <figure className="sp-hero-devices">
            <div className="sp-phone sp-phone-hero-back" aria-hidden="true">
              <img
                src="/images/spoolmark/qr-label-dark.webp"
                alt=""
                width="1206"
                height="2622"
              />
            </div>
            <div className="sp-phone sp-phone-hero-front">
              <img
                src="/images/spoolmark/inventory-light.webp"
                alt="Spoolmark inventory showing five usable spools grouped by material and their remaining filament"
                width="1206"
                height="2622"
                fetchPriority="high"
              />
            </div>
            <figcaption>
              <span>iPhone preview · Sample inventory</span>
              <span>Light &amp; dark themes</span>
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="sp-proof-strip" aria-label="Product commitments">
        <div className="shell sp-proof-strip-inner">
          <div>
            <CircleCheck size={18} aria-hidden="true" />
            <span>
              <strong>Local inventory</strong>
              <small>Stored on your device</small>
            </span>
          </div>
          <div>
            <ShieldCheck size={18} aria-hidden="true" />
            <span>
              <strong>No required account</strong>
              <small>Core use stays private</small>
            </span>
          </div>
          <div>
            <QrCode size={18} aria-hidden="true" />
            <span>
              <strong>Offline QR labels</strong>
              <small>Find the exact physical spool</small>
            </span>
          </div>
        </div>
      </section>

      <section className="sp-labels" id="features">
        <div className="shell sp-labels-grid">
          <div className="sp-labels-copy">
            <p className="sp-kicker">iPhone preview · Inventory labels</p>
            <h2>
              One label.
              <br />
              The right spool.
            </h2>
            <p className="sp-feature-lead">
              Two identical rolls can have very different stories. Give each
              physical spool its own QR label, then scan it to open that exact
              record—even offline.
            </p>
            <div className="sp-label-benefits">
              <article>
                <QrCode size={22} aria-hidden="true" />
                <div>
                  <h3>Scan straight back to your spool.</h3>
                  <p>
                    Find its remaining weight, location, and print history.
                    Reprint the same code whenever you need another copy.
                  </p>
                </div>
              </article>
              <article>
                <Printer size={22} aria-hidden="true" />
                <div>
                  <h3>Print one label or a whole shelf.</h3>
                  <p>
                    Export PDF labels in 50 × 30 or 60 × 40 mm sizes, or arrange
                    them on A4 and US Letter sheets. Adjust copies, margins,
                    gaps, and the starting position on a partly used sheet.
                  </p>
                </div>
              </article>
              <article>
                <DatabaseBackup size={22} aria-hidden="true" />
                <div>
                  <h3>Keep the link with your collection.</h3>
                  <p>
                    Labels belong to your local inventory. A JSON backup carries
                    those links with it when you restore on another device.
                  </p>
                </div>
              </article>
            </div>
          </div>
          <div className="sp-label-screens">
            <figure>
              <div className="sp-phone">
                <img
                  src="/images/spoolmark/qr-label-dark.webp"
                  alt="Spoolmark QR label linked to a Dark Red spool, with an option to share or reprint its PDF"
                  width="1206"
                  height="2622"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <figcaption>A label for one physical spool</figcaption>
            </figure>
            <figure>
              <div className="sp-phone">
                <img
                  src="/images/spoolmark/print-labels-light.webp"
                  alt="Spoolmark print labels screen with PDF layout sizes and copies for selected spools"
                  width="1206"
                  height="2622"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <figcaption>PDF layouts for your labels</figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="sp-walkthrough shell" id="inventory">
        <header className="sp-section-intro">
          <div>
            <p className="sp-kicker">Your filament inventory</p>
            <h2>
              Know what is
              <br />
              on your shelf.
            </h2>
          </div>
          <p>
            Start with the filament you already own. Find the right material
            and color, see where you stored it, and check how much is left
            before starting a print.
          </p>
        </header>

        <div className="sp-step-grid">
          {productSteps.map((step) => (
            <article className="sp-step" key={step.number}>
              <figure className="sp-step-visual">
                <div className="sp-phone sp-phone-step">
                  <img
                    src={step.image}
                    alt={step.alt}
                    width="1206"
                    height="2622"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <figcaption>{step.caption}</figcaption>
              </figure>
              <div className="sp-step-copy">
                <p className="sp-step-label">
                  <span>{step.number}</span>
                  {step.label}
                </p>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </div>
            </article>
          ))}
        </div>
        <p className="sp-capture-note">
          App screens show the iPhone preview with sample data, in light and
          dark themes.
        </p>
      </section>

      <section className="sp-collection" id="backups">
        <div className="shell sp-collection-grid">
          <figure className="sp-collection-visual">
            <div className="sp-phone">
              <img
                src="/images/spoolmark/backup-light.webp"
                alt="Spoolmark backup and restore screen with JSON backup and CSV inventory import and export"
                width="1206"
                height="2622"
                loading="lazy"
                decoding="async"
              />
            </div>
            <figcaption>Backup &amp; restore · iPhone preview</figcaption>
          </figure>
          <div className="sp-collection-copy">
            <p className="sp-kicker">Made to stay useful</p>
            <h2>
              Your collection.
              <br />
              On your terms.
            </h2>
            <div className="sp-collection-points">
              <article>
                <DatabaseBackup size={23} aria-hidden="true" />
                <h3>Take your inventory with you.</h3>
                <p>
                  Export a JSON backup with your spool records, label links, and
                  history. Review a merge or replacement before restoring. CSV
                  import and export let you work with inventory in a
                  spreadsheet; use JSON to preserve label links and history.
                </p>
              </article>
              <article>
                <ScanLine size={23} aria-hidden="true" />
                <h3>Tap a label. Open its record.</h3>
                <p>
                  Link a supported NFC tag to an inventory spool, or create a
                  compatible label. A guided batch flow walks you through
                  labeling multiple spools on supported hardware.
                </p>
              </article>
              <article>
                <Moon size={23} aria-hidden="true" />
                <h3>Comfortable in any light.</h3>
                <p>
                  Choose light, charcoal dark, or follow your system setting.
                  Your filament colors stay the same in either theme.
                </p>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section className="sp-platform" id="compatibility">
        <div className="shell">
          <header className="sp-platform-heading">
            <p className="sp-kicker">Platform support</p>
            <h2>One inventory. Platform-aware capture.</h2>
            <p>
              The product stays consistent while each phone uses the intake
              method its hardware supports.
            </p>
          </header>

          <div className="sp-platform-list">
            <article className="sp-platform-row">
              <div className="sp-platform-name">
                <span className="sp-inline-status">
                  <span aria-hidden="true" /> Available now
                </span>
                <h3>Android</h3>
              </div>
              <div className="sp-platform-copy">
                <h4>Scan supported tags directly.</h4>
                <p>
                  On compatible hardware, Spoolmark reads supported Bambu Lab
                  and OpenPrintTag records. Create Spoolmark companion labels on
                  NTAG215/216 stickers, or create and update supported
                  OpenPrintTag labels on compatible ICODE SLIX2 tags.
                </p>
              </div>
              <div className="sp-platform-action">
                <a
                  href={googlePlayUrl}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Get Spoolmark on Google Play (opens in a new tab)"
                >
                  Google Play <ArrowUpRight size={16} aria-hidden="true" />
                </a>
                <small>Listed as Spool Scanner (Spoolmark)</small>
              </div>
            </article>

            <article className="sp-platform-row" id="iphone-availability">
              <div className="sp-platform-name">
                <span className="sp-inline-status sp-inline-status-soon">
                  <span aria-hidden="true" /> App Store coming soon
                </span>
                <h3>iPhone</h3>
              </div>
              <div className="sp-platform-copy">
                <h4>Start with the camera. Add NFC when needed.</h4>
                <p>
                  The development build includes camera intake and QR labels
                  without an extra reader, plus compatible Spoolmark and
                  OpenPrintTag NFC labels. An optional ACS ACR1555U-A1 Bluetooth
                  integration is being prepared for read-only scans of original
                  Bambu Lab tags. Physical-device testing and reader review are
                  still pending; final support will be confirmed before release.
                </p>
              </div>
              <div className="sp-platform-action">
                <a href="/spoolmark/blog/bambu-lab-spool-scanning-on-ios">
                  How iPhone scanning works
                  <ArrowRight size={16} aria-hidden="true" />
                </a>
                <small>Public App Store release is not available yet</small>
              </div>
            </article>
          </div>

          <div className="sp-safety-note">
            <ShieldCheck size={20} aria-hidden="true" />
            <p>
              <strong>Bambu Lab tags remain read-only.</strong> Spoolmark does
              not modify, clone, or imitate them, and protected or unknown tags
              are left unchanged.
            </p>
          </div>

          <details className="sp-technical-details">
            <summary>Technical NFC details</summary>
            <div>
              <p>
                Original Bambu Lab tags use MIFARE Classic. Compatible Android
                hardware can read them directly, while built-in iPhone NFC
                cannot.
              </p>
              <p>
                In the iPhone development build, Spoolmark companion tags are
                read-only. Compatible
                OpenPrintTag filament tags support weight updates; creating a
                new OpenPrintTag label requires a blank, unprotected ICODE SLIX2
                tag. The same OpenPrintTag limits apply on Android.
              </p>
            </div>
          </details>
        </div>
      </section>

      <section className="sp-trust" id="trust">
        <div className="shell sp-trust-grid">
          <header>
            <p className="sp-kicker">Privacy and care</p>
            <h2>Local by default.</h2>
            <p>
              Privacy is part of the product architecture, not a setting you
              have to hunt for.
            </p>
          </header>

          <div className="sp-trust-points">
            <article>
              <ShieldCheck size={22} aria-hidden="true" />
              <h3>Your inventory stays yours.</h3>
              <p>
                Core use stores inventory on your device and needs no account.
              </p>
              <a href="/spoolmark/privacy">
                Read the privacy note
                <ArrowRight size={15} aria-hidden="true" />
              </a>
            </article>
            <article>
              <Camera size={22} aria-hidden="true" />
              <h3>Photos do not linger.</h3>
              <p>
                iPhone camera-intake photos are analyzed on-device and discarded
                afterward.
              </p>
            </article>
            <article>
              <Mail size={22} aria-hidden="true" />
              <h3>Support is direct.</h3>
              <p>
                Questions go to the person building and maintaining Spoolmark.
              </p>
              <a href="mailto:hello@alxnsv.io?subject=Spoolmark%20support">
                Email support
                <ArrowRight size={15} aria-hidden="true" />
              </a>
            </article>
          </div>
        </div>
      </section>

      <section className="sp-final">
        <div className="shell sp-final-grid">
          <div>
            <p className="sp-kicker">Ready for the next print</p>
            <h2>Know what you have before you start.</h2>
          </div>
          <div>
            <p>
              Get Spoolmark on Android today. The iPhone App Store listing is
              coming soon, beginning with a U.S. launch.
            </p>
            <StoreLinks label="Get Spoolmark before the next print" />
          </div>
        </div>
      </section>
    </main>
  );
}
